import { authenticationMessage, passwordResultMessage } from "./backend-auth";
import { Error as HttpError } from "./http";

export const bindPasswordGuidance = (newPassword: HTMLInputElement, check: () => Promise<Response>,
    dependencies: HTMLInputElement[] = []) => {
    const group = newPassword.parentElement!;
    newPassword.setAttribute(`aria-describedby`, [`lengde`, `styrke`, `kontroll`, `veiledning`]
        .map(part => `${newPassword.id}-${part}`).join(` `));
    newPassword.insertAdjacentHTML(`afterend`, `
            <p id="${newPassword.id}-lengde" data-password-lengde class="form-text"
                role="status" aria-atomic="true"></p>
            <label class="form-label" for="${newPassword.id}-styrkemaler">Anslått passordstyrke</label>
            <meter id="${newPassword.id}-styrkemaler" data-password-styrkemaler
                min="0" max="4" low="2" high="3" optimum="4" value="0"
                hidden aria-describedby="${newPassword.id}-styrke"></meter>
            <p id="${newPassword.id}-styrke" data-password-styrke role="status" aria-atomic="true">
                Skriv et passord for å se anslått styrke.</p>
            <p id="${newPassword.id}-kontroll" data-password-kontroll role="status" aria-atomic="true">
                Passordet er ikke kontrollert ennå.</p>
            <p id="${newPassword.id}-veiledning" class="form-text">Styrken er veiledende.
                Når anslått styrke er middels eller sterk, kontrolleres passordet også av tjenesten.
                Det lagres først når du velger knappen for å lagre.</p>`);
    const lengthStatus = group.querySelector<HTMLElement>(`[data-password-lengde]`)!;
    const updateLength = () => {
        // Match the server's NFC-normalized Unicode code-point count, including emoji.
        const length = Array.from(newPassword.value.normalize(`NFC`)).length;
        lengthStatus.textContent = length < 15 ? `${length} tegn – skriv minst ${15 - length} tegn til.` :
            length > 128 ? `${length} tegn – bruk høyst 128 tegn.` : `${length} tegn – lengden er godkjent.`;
        newPassword.parentElement!.classList.toggle(`field-error`, length > 128);
    };
    const strengthStatus = group.querySelector<HTMLElement>(`[data-password-styrke]`)!;
    const meter = group.querySelector<HTMLMeterElement>(`[data-password-styrkemaler]`)!;
    const checkedStatus = group.querySelector<HTMLElement>(`[data-password-kontroll]`)!;
    let strengthTimer = 0;
    let checkTimer = 0;
    let checksBlockedUntil = 0;
    let revision = 0;
    // Load dictionaries only when a password-choice form is initialized. Scoring stays local.
    const scorer = import(`./password-strength`).catch(() => null);
    const updateStrength = () => {
        const current = ++revision;
        window.clearTimeout(strengthTimer);
        window.clearTimeout(checkTimer);
        checkedStatus.textContent = `Passordet er ikke kontrollert ennå.`;
        checkedStatus.className = ``;
        meter.hidden = true;
        if (!newPassword.value) {
            strengthStatus.textContent = `Skriv et passord for å se anslått styrke.`;
            return;
        }
        if (Array.from(newPassword.value.normalize(`NFC`)).length > 128 || newPassword.value.length > 256) {
            strengthStatus.textContent = `Kort ned passordet til høyst 128 tegn for å se anslått styrke.`;
            return;
        }
        strengthStatus.textContent = `Vurderer styrken …`;
        strengthTimer = window.setTimeout(async () => {
            const module = await scorer;
            if (current !== revision || !newPassword.isConnected) return;
            if (!module) {
                strengthStatus.textContent = `Styrkevurderingen er utilgjengelig. Du kan fortsatt lagre passordet.`;
                return;
            }
            const result = module.estimatePasswordStrength(newPassword.value);
            meter.value = result.score;
            meter.hidden = false;
            strengthStatus.textContent = `${result.label}. ${result.advice}`;
            const length = Array.from(newPassword.value.normalize(`NFC`)).length;
            if (result.score < 3 || length < 15) return;
            if (dependencies.some(input => !input.value)) {
                checkedStatus.textContent = `Fyll ut nåværende passord for å kontrollere det nye.`;
                return;
            }
            if (Date.now() < checksBlockedUntil) {
                checkedStatus.textContent = `Vent litt før en ny forhåndskontroll. Passordet sjekkes også ved lagring.`;
                return;
            }
            checkTimer = window.setTimeout(async () => {
                if (current !== revision || !newPassword.isConnected) return;
                checkedStatus.textContent = `Kontrollerer passordet …`;
                try {
                    const response = await check();
                    const problem = await passwordResultMessage(response);
                    if (current !== revision || !newPassword.isConnected) return;
                    checkedStatus.textContent = problem ?? `Passordet er kontrollert og oppfyller kravene.`;
                    checkedStatus.className = problem ? `text-danger` : `text-success`;
                } catch (error) {
                    if (error instanceof HttpError && error.status === 429) checksBlockedUntil = Date.now() + 900000;
                    if (current !== revision || !newPassword.isConnected) return;
                    checkedStatus.textContent = `Passordet kunne ikke godkjennes: ${authenticationMessage(error)}`;
                    checkedStatus.className = `text-danger`;
                }
            }, 800);
        }, 200);
    };
    newPassword.addEventListener(`input`, updateLength);
    newPassword.addEventListener(`input`, updateStrength);
    newPassword.addEventListener(`change`, () => { updateLength(); updateStrength(); });
    updateLength();
    updateStrength();

    const refresh = () => { updateLength(); updateStrength(); };
    dependencies.forEach(input => {
        input.addEventListener(`input`, updateStrength);
        input.addEventListener(`change`, updateStrength);
    });
    newPassword.form!.addEventListener(`reset`, () => {
        cancel();
        queueMicrotask(refresh);
    });
    const cancel = () => {
        ++revision;
        window.clearTimeout(strengthTimer);
        window.clearTimeout(checkTimer);
        checkedStatus.textContent = `Passordet er ikke kontrollert ennå.`;
        checkedStatus.className = ``;
    };
    return cancel;
};
